import {
  collection, doc,
  addDoc, setDoc, updateDoc, deleteDoc,
  getDoc, query, orderBy, limit,
  onSnapshot, serverTimestamp, increment,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { db, auth, storage } from "./firebase";

const uid = () => auth.currentUser?.uid;
const userRef = () => doc(db, "users", uid());
const col = (path) => collection(db, path);

// ── USER PROFILE ──
export const createUserProfile = async (userData) => {
  await setDoc(userRef(), {
    ...userData,
    timerStreak: 0,
    totalSessions: 0,
    totalFocusMins: 0,
    budget: { total: 0, categories: [] },
    createdAt: serverTimestamp(),
  });
};

export const getUserProfile = async () => {
  const snap = await getDoc(userRef());
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const subscribeToProfile = (callback) => {
  return onSnapshot(userRef(), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
};

export const updateUserProfile = async (data) => {
  await updateDoc(userRef(), data);
};

export const updateBudget = async (budget) => {
  await updateDoc(userRef(), { budget });
};

export const saveTheme = async (theme) => {
  await updateDoc(userRef(), { theme });
};

// ── COURSES ──
const coursesRef = () => col(`users/${uid()}/courses`);

export const addCourse = async (course) => {
  return await addDoc(coursesRef(), { ...course, createdAt: serverTimestamp() });
};

export const subscribeToCourses = (callback) => {
  return onSnapshot(
    query(coursesRef(), orderBy("createdAt")),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

export const deleteCourse = async (courseId) => {
  await deleteDoc(doc(db, `users/${uid()}/courses`, courseId));
};

// ── TASKS ──
const tasksRef = () => col(`users/${uid()}/tasks`);

export const addTask = async (task) => {
  return await addDoc(tasksRef(), {
    name: task.name,
    courseId: task.courseId || null,
    priority: task.priority || "normal",
    due: task.due || null,
    done: false,
    doneAt: null,
    createdAt: serverTimestamp(),
  });
};

export const toggleTask = async (taskId, currentDone) => {
  await updateDoc(doc(db, `users/${uid()}/tasks`, taskId), {
    done: !currentDone,
    doneAt: !currentDone ? serverTimestamp() : null,
  });
};

export const deleteTask = async (taskId) => {
  await deleteDoc(doc(db, `users/${uid()}/tasks`, taskId));
};
export const editTask = async (taskId, updates) => {
  await updateDoc(doc(db, `users/${uid()}/tasks`, taskId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const subscribeToTasks = (callback) => {
  return onSnapshot(
    query(tasksRef(), orderBy("createdAt", "desc")),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

// ── NOTES ──
const notesRef = () => col(`users/${uid()}/notes`);

export const addNote = async (note) => {
  return await addDoc(notesRef(), {
    title: note.title,
    content: note.content || "",
    courseId: note.courseId || null,
    attachments: note.attachments || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const updateNote = async (noteId, data) => {
  await updateDoc(doc(db, `users/${uid()}/notes`, noteId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

export const deleteNote = async (noteId) => {
  await deleteDoc(doc(db, `users/${uid()}/notes`, noteId));
};

export const subscribeToNotes = (callback) => {
  return onSnapshot(
    query(notesRef(), orderBy("updatedAt", "desc")),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

// ── EXPENSES ──
const expensesRef = () => col(`users/${uid()}/expenses`);

export const addExpense = async (expense) => {
  return await addDoc(expensesRef(), {
    name: expense.name,
    amount: expense.amount,
    category: expense.category,
    icon: expense.icon || "💸",
    color: expense.color || "#22222C",
    createdAt: serverTimestamp(),
  });
};

export const deleteExpense = async (expenseId) => {
  await deleteDoc(doc(db, `users/${uid()}/expenses`, expenseId));
};

export const subscribeToExpenses = (callback) => {
  return onSnapshot(
    query(expensesRef(), orderBy("createdAt", "desc"), limit(50)),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
};

// ── FOCUS SESSIONS ──
const sessionsRef = () => col(`users/${uid()}/sessions`);
export const logFocusSession = async (durationMins, type = "pomodoro", taskId = null) => {
  await addDoc(sessionsRef(), {
    durationMins,
    type,
    taskId: taskId || null,
    createdAt: serverTimestamp(),
  });

  // If linked to a task — update task's logged time
  if (taskId) {
    const taskRef = doc(db, `users/${uid()}/tasks`, taskId);
    const taskSnap = await getDoc(taskRef);
    if (taskSnap.exists()) {
      const current = taskSnap.data().loggedMins || 0;
      await updateDoc(taskRef, {
        loggedMins: current + durationMins,
        hasSession: true,
      });
    }
  }

  const today = new Date().toDateString();
  const profile = await getUserProfile();

  if (!profile) {
    await setDoc(userRef(), {
      timerStreak: 1,
      totalSessions: 1,
      totalFocusMins: durationMins,
      lastSessionDate: today,
      createdAt: serverTimestamp(),
    });
    return;
  }

  const lastDate = profile?.lastSessionDate;
  let newStreak = profile?.timerStreak || 0;

  if (lastDate === new Date(Date.now() - 86400000).toDateString()) {
    newStreak += 1;
  } else if (lastDate !== today) {
    newStreak = 1;
  }

  await updateDoc(userRef(), {
    totalSessions: increment(1),
    totalFocusMins: increment(durationMins),
    timerStreak: newStreak,
    lastSessionDate: today,
  });
};

// ── STUDY PLAN ──
const planRef = (date) => doc(db, `users/${uid()}/plans`, date);

export const getTodayPlan = async () => {
  const today = new Date().toDateString();
  const snap = await getDoc(planRef(today));
  if (snap.exists()) return snap.data().sessions || [];

  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const ySnap = await getDoc(planRef(yesterday));
  if (ySnap.exists()) {
    const incomplete = (ySnap.data().sessions || []).filter(s => !s.done);
    if (incomplete.length > 0) {
      await setDoc(planRef(today), { sessions: incomplete, date: today });
      return incomplete;
    }
  }
  return [];
};

export const savePlan = async (sessions) => {
  const today = new Date().toDateString();
  await setDoc(planRef(today), { sessions, date: today });
};

export const subscribeToPlan = (callback) => {
  const today = new Date().toDateString();
  return onSnapshot(planRef(today), (snap) => {
    if (snap.exists()) callback(snap.data().sessions || []);
    else callback([]);
  });
};

// ── FILE UPLOADS ──
export const uploadNoteFile = (file, onProgress) => {
  return new Promise((resolve, reject) => {
    const path = `users/${uid()}/notes/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on("state_changed",
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        if (onProgress) onProgress(progress);
      },
      (error) => reject(error),
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({ url, path, name: file.name, type: file.type, size: file.size });
      }
    );
  });
};

export const deleteNoteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (err) {
    console.warn("File delete error:", err);
  }
};